/**
 * Lightweight motion streaks for falling objects.
 * Vector-only (gradients + ellipses) — no multi-pass sprite blur.
 * Shared by the live Fall stage (velocity-aligned) and the static logo (vertical).
 */
import { FALL_GRAVITY_REF_RUN_HEIGHT_PX } from "../config/constants.js";

export var TRAIL_LENGTH_MUL = 0.7;

/** Gem runway sprite — pink/violet, not cyan. */
var GEM_TRAIL_RGB = [232, 128, 218];
/** Merge pickups — one cool blue for every tier. */
var MERGE_TRAIL_RGB = [102, 182, 255];
/** Boulder — warm rock (matches sprite well). */
var BOULDER_TRAIL_RGB = [176, 136, 88];
/** Shield bonus — soft violet. */
var BONUS_TRAIL_RGB = [196, 132, 248];

function mixRgb(rgb, towardWhite, t) {
  t = Math.max(0, Math.min(1, t));
  return [
    Math.round(rgb[0] + (255 - rgb[0]) * t),
    Math.round(rgb[1] + (255 - rgb[1]) * t),
    Math.round(rgb[2] + (255 - rgb[2]) * t),
  ];
}

/**
 * Trail tint aligned with runway sprites (not tier palette for merge pickups).
 * @param {string} [objectType]
 * @returns {[number, number, number]}
 */
export function getFallMotionTrailTint(objectType) {
  if (objectType === "gem") return GEM_TRAIL_RGB;
  if (objectType === "merge_pickup") return MERGE_TRAIL_RGB;
  if (objectType === "boulder") return BOULDER_TRAIL_RGB;
  if (objectType === "bonus") return BONUS_TRAIL_RGB;
  return MERGE_TRAIL_RGB;
}

/**
 * Per-type trail span as a fraction of the object's draw width.
 * Gems keep a narrow streak; boulders and merge pickups span closer to the sprite.
 * @param {string} [objectType]
 * @returns {number}
 */
export function getFallMotionTrailWidthMul(objectType) {
  if (objectType === "gem" || objectType === "bonus") return 0.3;
  if (objectType === "boulder") return 0.58;
  if (objectType === "merge_pickup") return 0.52;
  return 0.4;
}

function readTrailLengthMul(opts) {
  return opts.lengthMul != null ? opts.lengthMul : TRAIL_LENGTH_MUL;
}

function readTrailMetrics(size, trailScale, lengthMul, trailWidth) {
  var trailLen = size * (2.6 + trailScale * 2.8) * lengthMul;
  var tailInset = size * 0.04;
  var span = trailWidth != null && isFinite(trailWidth) && trailWidth > 0
    ? trailWidth
    : size * 0.55;
  return { trailLen: trailLen, tailInset: tailInset, trailWidth: span };
}

function resolveTrailLength(metrics, headCx, headCy, dirX, dirY, clipTopY) {
  var trailLen = metrics.trailLen;
  if (clipTopY == null || !isFinite(clipTopY)) return trailLen;
  if (!(dirY > 0)) return trailLen;
  var tipY = headCy - dirY * trailLen;
  if (tipY >= clipTopY) return trailLen;
  return Math.max(0, headCy - clipTopY);
}

function resolveHeadAlpha(depth, prominence) {
  prominence = prominence != null && isFinite(prominence) ? prominence : 1;
  return Math.min(0.88, (0.28 + depth * 0.24) * prominence);
}

function fillCometCone(ctx, tipX, tipY, headCx, headCy, halfHead, headAlpha, tintRgb, perpX, perpY, glowMul) {
  var r = tintRgb[0];
  var g = tintRgb[1];
  var b = tintRgb[2];
  var hot = mixRgb(tintRgb, 1, 0.38);
  var half = halfHead * (glowMul != null ? glowMul : 1);
  var leftX = headCx + perpX * half;
  var leftY = headCy + perpY * half;
  var rightX = headCx - perpX * half;
  var rightY = headCy - perpY * half;
  var grad = ctx.createLinearGradient(tipX, tipY, headCx, headCy);
  grad.addColorStop(0, "rgba(" + r + "," + g + "," + b + ", 0)");
  grad.addColorStop(0.22, "rgba(" + r + "," + g + "," + b + ", " + (headAlpha * 0.14).toFixed(3) + ")");
  grad.addColorStop(0.58, "rgba(" + hot[0] + "," + hot[1] + "," + hot[2] + ", " + (headAlpha * 0.52).toFixed(3) + ")");
  grad.addColorStop(1, "rgba(" + hot[0] + "," + hot[1] + "," + hot[2] + ", " + (headAlpha * 0.78).toFixed(3) + ")");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(leftX, leftY);
  ctx.lineTo(rightX, rightY);
  ctx.closePath();
  ctx.fill();
}

/** Tapered comet tail: wide at the object, pointed opposite velocity. */
function drawCometTail(ctx, x, y, metrics, headAlpha, tintRgb, dirX, dirY, perpX, perpY, clipTopY) {
  var headCx = x - dirX * metrics.tailInset;
  var headCy = y - dirY * metrics.tailInset;
  var trailLen = resolveTrailLength(metrics, headCx, headCy, dirX, dirY, clipTopY);
  if (trailLen < 2) return;

  var tipX = headCx - dirX * trailLen;
  var tipY = headCy - dirY * trailLen;
  var halfHead = metrics.trailWidth * 0.48;

  ctx.save();
  fillCometCone(ctx, tipX, tipY, headCx, headCy, halfHead, headAlpha * 0.72, tintRgb, perpX, perpY, 1.14);
  fillCometCone(ctx, tipX, tipY, headCx, headCy, halfHead, headAlpha, tintRgb, perpX, perpY, 0.66);
  ctx.restore();
}

/**
 * Faint copies of the object along -velocity. Length stays tiny at low
 * intensity (flask bounce / hang) and grows with speed² so a full-speed fall
 * reads as a smear. Ghosts taper down — never larger than the live sprite.
 * Near-cardinal motion is snapped so Matter jitter does not slant a straight fall.
 * @returns {{ x: number, y: number, width: number, height: number, alpha: number }[]}
 */
export function layoutFallMotionBlurGhosts(x, y, width, height, opts) {
  opts = opts || {};
  var vx = opts.velocity && opts.velocity.x;
  var vy = opts.velocity && opts.velocity.y;
  if (vx == null || vy == null || !isFinite(vx) || !isFinite(vy)) return [];
  var intensity = opts.intensity != null ? Number(opts.intensity) : 1;
  if (!(intensity > 0)) return [];
  intensity = Math.min(1, intensity);
  var ax = Math.abs(vx);
  var ay = Math.abs(vy);
  if (ax < ay * 0.18) vx = 0;
  else if (ay < ax * 0.18) vy = 0;
  var speed = Math.sqrt(vx * vx + vy * vy);
  if (!(speed > 1e-6)) return [];
  var dirX = vx / speed;
  var dirY = vy / speed;
  var w = width > 0 ? width : 0;
  var h = height > 0 ? height : 0;
  var size = Math.max(w, h);
  var smear = size * (0.05 + intensity * intensity * 0.46);
  var n = opts.ghosts != null ? Math.max(1, Math.floor(opts.ghosts)) : 4;
  var out = [];
  for (var i = 1; i <= n; i++) {
    var t = i / (n + 0.2);
    var pull = smear * t;
    var taper = 1 - t * 0.48;
    out.push({
      x: x - dirX * pull,
      y: y - dirY * pull,
      width: Math.max(1, w * taper),
      height: Math.max(1, h * taper),
      alpha: intensity * 0.22 * Math.pow(0.6, i - 1),
    });
  }
  return out;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} size
 * @param {number} vx
 * @param {number} vy
 * @param {object} opts
 */
function drawVelocityFallMotionTrail(ctx, x, y, size, vx, vy, opts) {
  var trailScale = opts.trailScale != null ? opts.trailScale : 1;
  var depth = opts.depth != null ? opts.depth : 0.5;
  var tintRgb = opts.tintRgb || [255, 245, 220];
  var clipTopY = opts.clipTopY != null ? opts.clipTopY : -Infinity;
  var speed = Math.sqrt(vx * vx + vy * vy);
  if (!(speed > 1e-6)) return;

  var dirX = vx / speed;
  var dirY = vy / speed;
  var perpX = -dirY;
  var perpY = dirX;
  var metrics = readTrailMetrics(size, trailScale, readTrailLengthMul(opts), opts.trailWidth);
  var headAlpha = resolveHeadAlpha(depth, opts.prominence);
  drawCometTail(ctx, x, y, metrics, headAlpha, tintRgb, dirX, dirY, perpX, perpY, clipTopY);
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} size
 * @param {object} opts
 */
function drawVerticalFallMotionTrail(ctx, x, y, size, opts) {
  var trailScale = opts.trailScale != null ? opts.trailScale : 1;
  var depth = opts.depth != null ? opts.depth : 0.5;
  var tintRgb = opts.tintRgb || [255, 245, 220];
  var clipTopY = opts.clipTopY != null ? opts.clipTopY : 0;
  var metrics = readTrailMetrics(size, trailScale, readTrailLengthMul(opts), opts.trailWidth);
  var headAlpha = resolveHeadAlpha(depth, opts.prominence);
  drawCometTail(ctx, x, y, metrics, headAlpha, tintRgb, 0, 1, 1, 0, clipTopY);
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} size
 * @param {{
 *   trailScale?: number,
 *   depth?: number,
 *   prominence?: number,
 *   tintRgb?: [number, number, number],
 *   clipTopY?: number,
 *   lengthMul?: number,
 *   trailWidth?: number,
 *   velocity?: { x?: number, y?: number },
 * }} [opts]
 */
export function drawFallMotionTrail(ctx, x, y, size, opts) {
  opts = opts || {};
  var vx = opts.velocity && opts.velocity.x;
  var vy = opts.velocity && opts.velocity.y;
  if (vx != null && vy != null && isFinite(vx) && isFinite(vy)) {
    drawVelocityFallMotionTrail(ctx, x, y, size, vx, vy, opts);
    return;
  }
  drawVerticalFallMotionTrail(ctx, x, y, size, opts);
}

/**
 * Scale motion-trail speed thresholds with run height.
 * Matter velocity is px/tick and grows with gravity×distance (~run height).
 * Unscaled min/full speeds make small boards look sharp/fast (trail never fills).
 * @param {number} [runHeightPx]
 * @param {object} [runConfig]
 * @returns {number}
 */
export function fallMotionTrailSpeedScale(runHeightPx, runConfig) {
  var rc = runConfig || {};
  var ref = rc.FALL_GRAVITY_REF_RUN_HEIGHT_PX != null
    ? Number(rc.FALL_GRAVITY_REF_RUN_HEIGHT_PX)
    : FALL_GRAVITY_REF_RUN_HEIGHT_PX;
  if (!isFinite(ref) || ref <= 0) ref = FALL_GRAVITY_REF_RUN_HEIGHT_PX;
  var h = Number(runHeightPx);
  if (!isFinite(h) || h <= 0) h = ref;
  return Math.max(0.15, h / ref);
}

/**
 * @param {number} vx Matter.js body velocity.x (px per physics tick).
 * @param {number} vy Matter.js body velocity.y (px per physics tick).
 * @param {object} [runConfig]
 * @param {number} [runHeightPx]
 * @returns {number} 0–1 intensity for trail length/alpha.
 */
export function fallMotionTrailIntensity(vx, vy, runConfig, runHeightPx) {
  var rc = runConfig || {};
  var scale = fallMotionTrailSpeedScale(runHeightPx, rc);
  var minSpeed = rc.FALL_MOTION_TRAIL_MIN_VY != null
    ? Number(rc.FALL_MOTION_TRAIL_MIN_VY)
    : 2.5;
  var fullSpeed = rc.FALL_MOTION_TRAIL_FULL_VY != null
    ? Number(rc.FALL_MOTION_TRAIL_FULL_VY)
    : 9;
  if (!isFinite(minSpeed) || minSpeed < 0) minSpeed = 2.5;
  if (!isFinite(fullSpeed) || fullSpeed <= minSpeed) fullSpeed = minSpeed + 6.5;
  minSpeed *= scale;
  fullSpeed *= scale;
  var speed = Math.sqrt(
    (isFinite(vx) ? vx * vx : 0) + (isFinite(vy) ? vy * vy : 0)
  );
  if (!(speed > minSpeed)) return 0;
  return Math.min(1, (speed - minSpeed) / (fullSpeed - minSpeed));
}

/**
 * @param {object} [runConfig]
 * @param {{ forceReducedMotion?: boolean }} [ctx]
 * @returns {boolean}
 */
export function isFallMotionTrailEnabled(runConfig, ctx) {
  ctx = ctx || {};
  if (ctx.forceReducedMotion) return false;
  var rc = runConfig || {};
  var enabled = rc.FALL_MOTION_TRAIL_ENABLED != null
    ? rc.FALL_MOTION_TRAIL_ENABLED
    : true;
  return !!enabled;
}